import { createSlice,createAsyncThunk, isRejectedWithValue } from "@reduxjs/toolkit";
import { RootState } from "../../../Redux/store";

export interface PushNotif {
    view: 'dashboard' | 'orders' | 'nudges';
    data?: string;
    order?: string;
    filter?: string;
    id: string;
    title: string;
    message: string;
    errorMessage?: string;
};

export type notifArgs = {
    id: string | string[]; 
};

export const addDevice = createAsyncThunk<void, string, {state: RootState, rejectValue: string}>(
    'notifications/addDevice',
    async(fcmToken, { getState, rejectWithValue }) => {
        try {
            const deviceUrl = 'https://customer.hirsch.com/v1/device';
            const token = getState().auth.token;
    
            const res = await fetch(deviceUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': btoa(`Bearer ${token}`)
                },
                body: JSON.stringify({ device: fcmToken })
            })
    
            if (!res.ok) return rejectWithValue(`Send Device Token Failed: ${res.status}`);
    
            const json = await res.json();
            console.log('Device URL Response:', json);
    
            if (json.errorMessage) return rejectWithValue(`Send Device Token Error: ${json.errorMessage}`);
        } catch (err) {
            console.error('ADD DEVICE ERROR:', err);
            return rejectWithValue(`Failed to register device`);
        }
    }
);

export const getNotifs = createAsyncThunk<PushNotif[], void, { state: RootState, rejectValue: string }>(
    'notifications/getNotifs',
    async (_, { getState, rejectWithValue }) => {
        try {
            const baseUrl = `https://customer.hirsch.com/notifications/v2`
            const token = getState().auth.token;

            if (!token) return rejectWithValue('No Token Found');

            const res = await fetch(baseUrl, {
                method: 'GET',
                headers: {
                    'content-type': 'application/json',
                    'Authorization': btoa(`Bearer ${token}`)
                }
            })

            if (!res.ok) throw new Error('Failed to fetch');

            const json = await res.json();

            if (!json.ack) {
                console.error('Get Notifications Error:', json.errorMessage);
                return rejectWithValue(json.errorMessage);
            };

            console.log('NOTIFS:', json);
            return json.data

        } catch (err: any) {
            return rejectWithValue(err.message);
        }
    }
);

export const deleteNotifs = createAsyncThunk<
    string[],
    notifArgs,
    { state: RootState; rejectValue: string }
>(
    'notifications/deleteNotifs',
    async (args, { getState, rejectWithValue }) => {
        try {
            const baseUrl = `https://customer.hirsch.com/notifications/v2`;
            const token = getState().auth.token;

            if (!token) {
                return rejectWithValue('No Token Found');
            }

            const messageIds: string[] = Array.isArray(args.id)
                ? args.id
                : [args.id];
            
                console.log('MessageIds:', messageIds);

            const res = await fetch(baseUrl, {
                method: 'DELETE',
                headers: {
                    'content-type': 'application/json',
                    'Authorization': btoa(`Bearer ${token}`)
                },
                body: JSON.stringify({
                    notificationId: messageIds
                })
            });

            if (!res.ok) {
                throw new Error('Failed to delete notification');
            }

            const json = await res.json();

            if (!json.ack) {
                return rejectWithValue(
                    json.errorMessage || 'Failed to delete notification'
                );
            }

            return messageIds;

        } catch (err: any) {
            return rejectWithValue(err.message);
        }
    }
);

type NotificationState = {
    pushNotifArr: PushNotif [];
    loading: boolean;
    deleting: boolean;
    error: string | undefined | boolean | null;
}

const initialState: NotificationState = {
    pushNotifArr: [],
    loading: false,
    deleting: false,
    error: null,
};

const notificationSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getNotifs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getNotifs.fulfilled, (state, action) => {
                state.pushNotifArr = action.payload;
                state.loading = false;
                state.error = null;
            })
            .addCase(getNotifs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error ? action.error.message : 'Failed to fetch Notifications';
            })

            .addCase(deleteNotifs.pending, (state) => {
                state.deleting = true;
                state.error = null;
            })

            .addCase(deleteNotifs.fulfilled, (state, action) => {
                state.pushNotifArr = state.pushNotifArr.filter(
                    notif => !action.payload.includes(notif.id)
                );

                state.deleting = false;
                state.error = null;
            })

            .addCase(deleteNotifs.rejected, (state, action) => {
                state.deleting = false;
                state.error = 
                    action.error
                    ? action.error.message
                    : 'Failed to delete notification';
            })
    }
});

export const notificationReducer = notificationSlice.reducer;